import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField, useMediaQuery, ToggleButton, ToggleButtonGroup, MenuItem } from '@mui/material';
import theme from '../../util/theme/theme';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';
import { AddonConfig, CapacityKind, addonLabel, STORAGE_SIZES_MB, formatStorage, HORIZON_STEPS_DAYS, formatHorizon } from '../../util/subscription-tiers';
import Toggle from '../ui/toggle';

export type NewCapacityAddon = { kind: CapacityKind; amount: number; pricePerMonth: number; pricePerYear: number; isOffered: boolean };

type EditSubscriptionAddonModalProps = {
    open: boolean;
    // null with onCreate = "Add add-on" (a new capacity add-on).
    addon: AddonConfig | null;
    saving?: boolean;
    onClose: () => void;
    onSave: (updated: AddonConfig) => void;
    onCreate?: (addon: NewCapacityAddon) => void;
};

const EMPTY_FORM: AddonConfig = { addonKey: 'users-0', isOffered: true, pricePerMonth: 0, pricePerYear: 0 };

// One add-on's catalog entry - Admin > Subscription Add-ons. Also adds a new
// capacity add-on (more users / a longer projection horizon).
const EditSubscriptionAddonModal: React.FC<EditSubscriptionAddonModalProps> = ({ open, addon, saving = false, onClose, onSave, onCreate }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isCreate = !addon && !!onCreate;
    const [form, setForm] = useState<AddonConfig | null>(null);
    const [kind, setKind] = useState<CapacityKind>('users');
    const [amount, setAmount] = useState('');

    useEffect(() => {
        setForm(isCreate ? EMPTY_FORM : addon);
        setKind('users');
        setAmount('');
    }, [addon, open, isCreate]);

    if (!form) return null;

    const label = addonLabel(form.addonKey);
    const setPrice = (field: 'pricePerMonth' | 'pricePerYear', value: string) =>
        setForm({ ...form, [field]: Math.max(0, Number(value) || 0) });

    const content = (
        <>
            <DialogTitle variant="h6" fontWeight={600}>
                {isCreate ? 'Add add-on' : `Edit ${label} add-on`}
            </DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={3} mt={1}>
                    {isCreate && (
                        <>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={kind}
                                onChange={(_, value) => { if (value) { setKind(value); setAmount(''); } }}
                                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', flex: 1 } }}
                            >
                                <ToggleButton value="users">More users</ToggleButton>
                                <ToggleButton value="horizon">Longer horizon</ToggleButton>
                                <ToggleButton value="storage">More storage</ToggleButton>
                            </ToggleButtonGroup>
                            <Box>
                                <Typography mb={1} fontSize={14} color="var(--color-text-muted)">
                                    {kind === 'users' ? 'Extra users' : kind === 'horizon' ? 'Projection horizon' : 'Extra Documents storage'}
                                </Typography>
                                {kind === 'users' ? (
                                    <TextField
                                        type="number"
                                        value={amount}
                                        onChange={e => setAmount(e.target.value)}
                                        size="small"
                                        fullWidth
                                        inputProps={{ min: 1 }}
                                    />
                                ) : (
                                    // Fixed sizes / lengths only.
                                    <TextField
                                        select
                                        value={amount}
                                        onChange={e => setAmount(e.target.value)}
                                        size="small"
                                        fullWidth
                                    >
                                        {kind === 'storage'
                                            ? STORAGE_SIZES_MB.map(mb => <MenuItem key={mb} value={String(mb)}>{formatStorage(mb)}</MenuItem>)
                                            : HORIZON_STEPS_DAYS.map(days => <MenuItem key={days} value={String(days)}>{formatHorizon(days)}</MenuItem>)}
                                    </TextField>
                                )}
                            </Box>
                        </>
                    )}
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography fontSize={14}>Offered as an add-on</Typography>
                        <Toggle checked={form.isOffered} onChange={(checked) => setForm({ ...form, isOffered: checked })} size="small" />
                    </Box>
                    <Box display="flex" gap={2}>
                        <Box flex={1}>
                            <Typography mb={1} fontSize={14} color="var(--color-text-muted)">No commitment, per month ($)</Typography>
                            <TextField
                                type="number"
                                value={form.pricePerMonth}
                                onChange={e => setPrice('pricePerMonth', e.target.value)}
                                size="small"
                                fullWidth
                                inputProps={{ min: 0 }}
                            />
                        </Box>
                        <Box flex={1}>
                            <Typography mb={1} fontSize={14} color="var(--color-text-muted)">12 months up front, per year ($)</Typography>
                            <TextField
                                type="number"
                                value={form.pricePerYear}
                                onChange={e => setPrice('pricePerYear', e.target.value)}
                                size="small"
                                fullWidth
                                inputProps={{ min: 0 }}
                            />
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{ background: 'var(--color-container-background)', color: 'var(--color-default-text)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={() => (isCreate
                        ? onCreate?.({ kind, amount: parseInt(amount, 10) || 0, pricePerMonth: form.pricePerMonth, pricePerYear: form.pricePerYear, isOffered: form.isOffered })
                        : onSave(form))}
                    disabled={saving || (isCreate && !(parseInt(amount, 10) > 0))}
                    variant="contained"
                    sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none' }}
                >
                    {isCreate ? 'Add' : 'Save Changes'}
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={onClose} heightVh={60}>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ '& .MuiPaper-root': { borderRadius: 3 } }}>
            {content}
        </Dialog>
    );
};

export default EditSubscriptionAddonModal;
